import Flickity from 'flickity'

export class RecommendationsElement extends HTMLElement {
  private flickity?: Flickity

  connectedCallback() {
    this.initializeRecommendations()
  }

  initializeRecommendations() {
    const url = this.dataset.url

    if (!url) {
      return
    }

    const checkVisibility = () => {
      const tabPanel = this.closest('[role="tabpanel"]')
      const isInTab = !!tabPanel
      const isTabVisible = !isInTab || !tabPanel.hasAttribute('hidden')

      if (!isInTab) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return
          observer.unobserve(this)
          this.loadRecommendations()
        }, { rootMargin: '0px 0px 200px 0px' })
        observer.observe(this)
      } else if (isTabVisible) {
        this.loadRecommendations()
      } else {
        const tabChangeObserver = new MutationObserver(() => {
          if (!tabPanel.hasAttribute('hidden')) {
            tabChangeObserver.disconnect()
            this.loadRecommendations()
          }
        })
        tabChangeObserver.observe(tabPanel, { attributes: true, attributeFilter: ['hidden'] })
      }
    }

    checkVisibility()
  }

  async loadRecommendations() {
    let url = this.dataset.url

    if (!url) {
      return
    }

    const cacheBuster = Date.now()
    url += (url.includes('?') ? '&' : '?') + `_=${cacheBuster}`

    try {
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const text = await response.text()

      const html = document.createElement('div')
      html.innerHTML = text

      const blockId = this.closest('[data-id]')?.getAttribute('data-id')

      const recommendationsSection = html.querySelector('section[id*="carousel_recommendations"], section[id*="tabbed_recommendations"]')

      if (!recommendationsSection) {
        this.showError()
        return
      }

      const blockElement = blockId ? recommendationsSection.querySelector(`[data-id="${blockId}"]`) : null

      const searchRoot = blockElement || recommendationsSection

      const apiCarousel = searchRoot.querySelector('x-carousel[data-api-products]')

      if (apiCarousel) {
        this.replaceWithApiCarousel(apiCarousel)
        return
      }

      const productCards = searchRoot.querySelectorAll('[data-product-id]')

      if (productCards.length === 0) {
        this.remove()
        return
      }

      this.createCarousel(productCards)
    } catch {
      this.showError()
    }
  }

  createCarousel(productCards: NodeListOf<Element>) {
    this.innerHTML = ''

    const carousel = document.createElement('div')
    carousel.className = this.dataset.carouselClasses || ''

    productCards.forEach((card) => {
      const wrapper = document.createElement('div')
      wrapper.className = this.dataset.productCardClasses || ''
      wrapper.appendChild(card.cloneNode(true))
      carousel.appendChild(wrapper)
    })

    this.appendChild(carousel)
    this.updateLazyLoading()

    let carouselSettings = {}
    try {
      if (this.dataset.carouselSettings) {
        carouselSettings = JSON.parse(this.dataset.carouselSettings)
      }
    } catch {
    }

    const defaultOptions = {
      contain: true
    }

    const options = { ...defaultOptions, ...carouselSettings }

    try {
      this.flickity = new Flickity(carousel, options)

      this.dispatchEvent(new CustomEvent('recommendations:loaded', {
        bubbles: true,
        detail: {
          carousel,
          flickity: this.flickity,
          productCount: productCards.length
        }
      }))
    } catch {
      this.showError()
    }
  }

  replaceWithApiCarousel(apiCarousel: Element) {
    const carouselClone = apiCarousel.cloneNode(true) as HTMLElement

    if (this.parentNode) {
      this.parentNode.replaceChild(carouselClone, this)
      this.updateLazyLoading()

      window.dispatchEvent(new CustomEvent('recommendations:loaded', {
        bubbles: true,
        detail: {
          carousel: carouselClone,
          source: 'api-rendered'
        }
      }))
    }
  }

  updateLazyLoading() {
    if (typeof window !== 'undefined' && (window as any).lazyLoadInstance) {
      try {
        (window as any).lazyLoadInstance.update()
      } catch {
      }
    }
  }

  showError() {
    this.remove()
  }

  disconnectedCallback() {
    if (this.flickity) {
      this.flickity.destroy()
    }
  }
}
